<i18n src="@/common/i18n/i18n-locales.json"></i18n>

<template>
  <f7-sheet style="height: 75%;" @sheet:closed="onPageClosed">
    <f7-toolbar :color-theme="themeColor">
      <f7-link>{{ $t(`pageTitle.diningPreference`) }}</f7-link>
      <f7-link sheet-close>{{ $t(`instruction.ok`) }}</f7-link>
    </f7-toolbar>
    <div style="display: flex; flex-direction: column; height: 100%;">
      <f7-block style="flex: 0 0 auto; margin: 25px 0px 10px 0px;">
        <f7-row no-gap>
          <f7-col width="33">
            <div
              :style="{ color: diningPreference === diningPreferenceType.forHere ? '#4CAF50' : '#888888' }"
              style="display: flex; align-items: center; justify-content: center; flex-direction: column;"
              @click="setForHereOnClick">
              <font-awesome-icon icon="utensils" style="font-size: 40px;"/>
              <span style="line-height: 32px;">{{ $t(`diningPreferenceType.${diningPreferenceType.forHere}`) }}</span>
            </div>
          </f7-col>
          <f7-col width="33">
            <div
              :style="{ color: diningPreference === diningPreferenceType.takeAway ? '#4CAF50' : '#888888' }"
              style="display: flex; align-items: center; justify-content: center; flex-direction: column;"
              @click="setDiningPreference(diningPreferenceType.takeAway)">
              <font-awesome-icon icon="shopping-bag" style="font-size: 40px;"/>
              <span style="line-height: 32px;">{{ $t(`diningPreferenceType.${diningPreferenceType.takeAway}`) }}</span>
            </div>
          </f7-col>
          <f7-col width="33">
            <div
              :style="{ color: diningPreference === diningPreferenceType.preOrder ? '#4CAF50' : '#888888' }"
              style="display: flex; align-items: center; justify-content: center; flex-direction: column;"
              @click="setPreOrderOnClick">
              <font-awesome-icon :icon="['far', 'calendar-alt']" style="font-size: 40px;"/>
              <span style="line-height: 32px;">{{ $t(`diningPreferenceType.${diningPreferenceType.preOrder}`) }}</span>
            </div>
          </f7-col>
        </f7-row>
      </f7-block>
      <f7-list v-if="diningPreference === diningPreferenceType.forHere" :color-theme="themeColor" style="flex: 1 1 auto; width: 100%; margin: 0px; overflow: auto;">
        <f7-list-item
          v-for="(tn) in tableNumbers"
          :key="`tableNumber-${tn}`"
          radio
          :title="tn"
          :checked="tn === tableNumber"
          @click="setTableNumber(tn)" />
      </f7-list>
      <div
        v-else-if="diningPreference === diningPreferenceType.preOrder"
        id="picker-date-container"
        style="box-sizing: border-box; width: 100%; border: solid 1px #EEEEEE; border-width: 1px 0px;" />
    </div>
  </f7-sheet>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { storeClient } from '@/service/apiClients'

export default {
  dependencies: ['themeColor', 'diningPreferenceType'],
  data () {
    return {
      timePicker: null,
      businessHoursTable: null,
      acceptDaysAmount: 2 // TODO: 之後這個就不需要傳參數了，參數應該存在 server 上面
    }
  },
  async mounted () {
    this.getTableNumbers()
    const { data: businessHoursTable } = await storeClient
      .get(`/stores/${this.storeId}/businessHours?v=${this.menuVariation}&s=${moment().format('YYYY-MM-DD')}&e=${moment().add(this.acceptDaysAmount - 1, 'd').format('YYYY-MM-DD')}`)
    this.businessHoursTable = businessHoursTable
    if (this.diningPreference === this.diningPreferenceType.preOrder) {
      this.renderTimePicker()
    }
  },
  computed: {
    ...mapState([
      'store',
      'menuVariation',
      'diningPreference',
      'tableNumbers',
      'tableNumber',
      'pickupTime',
      'phoneNumber'
    ]),
    ...mapGetters(['storeId', 'isPhoneNumberValid', 'isPayOnlyLinePay'])
  },
  methods: {
    ...mapActions([
      'getTableNumbers',
      'setTableNumber',
      'setDiningPreference',
      'setPickupTime'
    ]),
    maxMoment (...args) {
      const moments = args.filter(m => m instanceof moment)
      return moments.length === 0 ? null : moments.reduce((a, b) => a.isAfter(b) ? a : b, moments[0])
    },
    minMoment (...args) {
      const moments = args.filter(m => m instanceof moment)
      return moments.length === 0 ? null : moments.reduce((a, b) => a.isBefore(b) ? a : b, moments[0])
    },
    generateHours (start = 0, end = 23) {
      const arr = []
      for (let i = start; i <= end; i++) {
        arr.push(i < 10 ? '0' + i : i)
      }
      return arr
    },
    onPageClosed () {
      if (this.isPayOnlyLinePay === true) {
        this.$f7.dialog.alert('預約外帶僅支援LINE Pay 支付')
      }
    },
    generateMinutes (start = 0, end = 55) {
      const arr = []
      for (let i = start - start % 5; i <= end; i += 5) {
        arr.push(i < 10 ? '0' + i : i)
      }
      return arr
    },
    getAvailableHours (dateString, availableFrom) {
      return this.businessHoursTable[dateString]
        .reduce((r, [start, end]) => { // 營業時段 (ex. 午餐、晚餐)
          const startMoment = moment(dateString + ' ' + start, 'YYYY-MM-DD HH:mm')
          const endMoment = moment(dateString + ' ' + end, 'YYYY-MM-DD HH:mm')
          if (availableFrom > endMoment) { return r }
          const hourStart = startMoment > availableFrom
            ? parseInt(startMoment.format('H'))
            : parseInt(availableFrom.format('H'))
          const hourEnd = parseInt(endMoment.format('H'))
          return r.concat(this.generateHours(hourStart, hourEnd))
        }, [])
    },
    getAvailableMinutes (dateString, hourString, availableFrom) {
      return this.businessHoursTable[dateString]
        .reduce((targetMoment, [start, end]) => {
          const startMoment = moment(dateString + ' ' + start, 'YYYY-MM-DD HH:mm') // 時段的開始
          const endMoment = moment(dateString + ' ' + end, 'YYYY-MM-DD HH:mm')
          const selectedHourMoment = moment(dateString + ' ' + hourString + ':00', 'YYYY-MM-DD HH:mm')
          if (targetMoment || availableFrom > endMoment || selectedHourMoment > endMoment) {
            return targetMoment
          }
          const minuteStart = moment(this.maxMoment(startMoment, availableFrom, selectedHourMoment))
          const minuteEnd = moment(this.minMoment(endMoment, minuteStart.clone().endOf('hours')))
          return this.generateMinutes(minuteStart.format('m'), minuteEnd.format('m'))
        }, null)
    },
    showPaymentMethodAction () {
      this.$f7router.navigate(`/stores/${this.storeId}/order/select-payment-method-actions`)
    },
    setForHereOnClick () {
      this.setDiningPreference(this.diningPreferenceType.forHere)
    },
    setPreOrderOnClick () {
      if (!this.store.isSupportTakeout) {
        return this.$f7.dialog.alert('目前本店暫不接收預約外帶')
      }
      if (!this.businessHoursTable) {
        this.$f7.dialog.alert('僅開放前一天訂餐', '尚未開放')
        return
      }
      this.setDiningPreference(this.diningPreferenceType.preOrder)
      this.renderTimePicker()
    },
    diningPreferenceButtonStyle (diningPreference) {
      return diningPreference === this.diningPreference
        ? { 'color': '#FFFFFF', 'background-color': '#4caf50' }
        : { 'color': '#000000', 'background-color': '#FFFFFF' }
    },
    renderTimePicker () {
      this.$nextTick(() => {
        const that = this
        const availableFrom = moment().add(this.store.preOrderLatestOrderBoundaryMinutes + 5, 'minutes')
        const dates = [...Array(this.acceptDaysAmount).keys()].map(k => moment().add(k, 'd')).filter(d => {
          const dayHours = this.businessHoursTable[d.format('YYYY-MM-DD')]
          return dayHours.length !== 0 && availableFrom < moment(d.format('YYYY-MM-DD') + ' ' + dayHours[dayHours.length - 1][1])
        }).map(d => d.format('YYYY-MM-DD'))

        if (dates.length === 0) {
          this.timePicker = null
        } else {
          const initHours = this.getAvailableHours(dates[0], availableFrom)
          const initMinutes = this.getAvailableMinutes(dates[0], initHours[0], availableFrom)

          const timePickerObj = {
            containerEl: '#picker-date-container',
            inputEl: '#',
            toolbar: false,
            rotateEffect: true,
            cols: [
              {
                values: dates,
                // displayValues: [`今天 (${today.format('MM/DD')})`, `明天 (${tomorrow.format('MM/DD')})`],
                onChange: function (picker, value) {
                  if (picker.cols.length > 0 && picker.cols[2].replaceValues && picker.cols[4].replaceValues) {
                    const hours = that.getAvailableHours(value, availableFrom)
                    picker.cols[2].replaceValues(hours)

                    const hourString = moment(value + ' ' + that.businessHoursTable[value][0][0], 'YYYY-MM-DD HH:mm').format('H')
                    const minutes = that.getAvailableMinutes(value, hourString, availableFrom)
                    picker.cols[4].replaceValues(minutes)
                  }
                }
              },
              { divider: true, content: ' ' },
              {
                values: initHours,
                onChange: function (picker, value) {
                  if (picker.cols.length > 0 && picker.cols[4].replaceValues) {
                    const minutes = that.getAvailableMinutes(picker.cols[0].value, value, availableFrom)
                    picker.cols[4].replaceValues(minutes)
                  }
                }
              },
              { divider: true, content: ':' },
              { values: initMinutes }
            ],
            on: {
              change: function (picker, values, displayValues) {
                that.setPickupTime(moment(values[0] + 'T' + values[1] + ':' + values[2]).toISOString())
              }
            }
          }

          if (this.pickupTime) {
            const pickupTimeMoment = moment(this.pickupTime)
            this.timePicker = this.$f7.picker.create(Object.assign(timePickerObj, {
              value: [pickupTimeMoment.format('YYYY-MM-DD'), pickupTimeMoment.format('HH'), pickupTimeMoment.format('mm')]
            }))
          } else {
            this.timePicker = this.$f7.picker.create(timePickerObj)
          }
          this.timePicker.resizeCols()
        }
      })
    }
  }
}
</script>
